import * as React from 'react'
import styled from 'styled-components'

import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'

type AboutOwnerProps = {
  className?: string
  project: models.project.Project
}

type ThirdPartyGlyphs = {
  [key: string]: React.ComponentType<{ size: number }>
}

function AboutOwnerComponent({ className, project }: AboutOwnerProps): React.ReactElement<AboutOwnerProps> {
  const thirdPartyGlyphs = plume.glyphs.thirdParty as ThirdPartyGlyphs

  return (
    <InfoCard className={className}>
      <InfoCardItem>
        <plume.Link href={project.owner.absolute_url}>
          <plume.styles.image.Avatar src={models.user.avatar(project.owner, '90x90')} type="large" />
        </plume.Link>
        <InfoCardContent>
          <plume.Link href={project.owner.absolute_url}>
            <plume.styles.heading.XS>{project.owner.screenname}</plume.styles.heading.XS>
          </plume.Link>
          <InfoCardTags>
            {(project.city || project.owner.location) && (
              <InfoCardTag>
                <plume.glyphs.stroke.MapCursor size={18} />
                {project.city || project.owner.location}
              </InfoCardTag>
            )}
            {project.main_tag && (
              <plume.Link kind="secondary" withIcon href={project.main_tag?.absolute_url}>
                <InfoCardTag>
                  <plume.glyphs.stroke.Tag size={18} />
                  {models.project.mainTag(project)}
                </InfoCardTag>
              </plume.Link>
            )}
          </InfoCardTags>
          {project.links && (
            <InfoCardSocials>
              {project.links.map((link) => {
                const ThirdPartyGlyph =
                  thirdPartyGlyphs[link.service_type.charAt(0).toUpperCase() + link.service_type.slice(1)]
                if (!ThirdPartyGlyph) {
                  return (
                    <plume.Link key={link.id} href={link.url} target="_blank" rel="noopener noreferrer">
                      <plume.glyphs.thirdParty.WebsiteLink size={18} />
                    </plume.Link>
                  )
                }
                return (
                  <plume.Link key={link.id} href={link.url} target="_blank" rel="noopener noreferrer">
                    <ThirdPartyGlyph size={18} />
                  </plume.Link>
                )
              })}
            </InfoCardSocials>
          )}
        </InfoCardContent>
      </InfoCardItem>
    </InfoCard>
  )
}

export const AboutOwner = styled(AboutOwnerComponent)``

const InfoCard = styled.div<{ className?: string }>`
  margin-inline: auto;
  width: fit-content;
  margin-bottom: 40px;
`

const InfoCardTag = styled(plume.styles.heading.XXXXXS)`
  display: flex;
  align-items: center;
  gap: 2px;

  ${plume.glyphs.SizedSvg} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
  }
`

const InfoCardTags = styled.div`
  display: flex;
  margin-top: 4px;

  ${plume.Link} {
    cursor: pointer;
  }

  div + ${plume.Link} {
    margin-left: 17px;
  }
`

const InfoCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${plume.styles.heading.XS}::first-letter {
    text-transform: uppercase;
  }
`

const InfoCardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  ${plume.styles.image.Avatar} {
    width: 72px; //  HACK : overriding the standard size
    height: 72px; //  HACK : overriding the standard size
    background-color: ${plume.COLORS.PRIMARY_GREY_000};
  }
`

const InfoCardSocials = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  flex-wrap: wrap;
  gap: 10px;

  ${plume.glyphs.SizedSvg} {
    color: ${plume.COLORS.PRIMARY_SAND_700};
  }
`
